import {
  Center,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useFetchAccountsQuery } from 'api';
import { useAuth } from 'core';
import Decimal from 'decimal.js';
import {
  CurrencyLabel,
  FormButtonGroup,
  SubAccountLabel,
} from 'features/components';
import { every } from 'lodash';
import { useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { FiEdit, FiPlus } from 'react-icons/fi';
import {
  AfeBudgetRevisionAccountDrawer,
  AfeBudgetRevisionAccountDrawerProps,
} from '.';
import { PostAfeTransactionRequest } from 'api/bindings/PostAfeTransactionRequest';

export interface AfeFormRevisionDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit'> {
  afeId: string | undefined;
  onSubmit: (form: Form) => void;
  onClose: () => void;
}

type Form = PostAfeTransactionRequest;

export const AfeFormRevisionDrawerContent = (
  props: AfeFormRevisionDrawerContentProps
) => {
  const { afeId, onSubmit, onClose, ...rest } = props;

  // Fetches
  const auth = useAuth();
  const { data: accounts } = useFetchAccountsQuery(
    { clientId: auth?.user.clientId ?? "0" },
    { skip: !auth?.user.clientId }
  );

  // State
  const form = useForm<Form>({
    mode: 'all',
  });
  const { control, watch, register, handleSubmit } = form;
  const accountsField = useFieldArray({
    control,
    name: 'lineItems',
    keyName: '_id',
    rules: {
      // minLength: {value: 1, message: 'At least one row is required'},
      validate: {
        required: (v) => v?.length >= 0 || 'At least one row is required',
        nonZero: (v) =>
          every(
            v,
            (x) =>
              x.subAccountId &&
              (new Decimal(x.budgetedAmount || '0').gt(0) ||
                new Decimal(x.contingencyAmount || '0').gt(0))
          ) || 'At least one row is missing an account or is of zero value',
      },
    },
  });
  const [drawerSettings, setDrawerSettings] = useState<
    Omit<AfeBudgetRevisionAccountDrawerProps, 'onClose' | 'onSubmit'> & {
      index: number;
    }
  >();

  const revisionName = watch('name');

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {drawerSettings && (
          <AfeBudgetRevisionAccountDrawer
            {...drawerSettings}
            isOpen={true}
            onClose={() => setDrawerSettings(undefined)}
            onSubmit={(f) => {
              if (drawerSettings.index === -1) {
                accountsField.append(f);
              } else {
                accountsField.remove(drawerSettings.index);
                accountsField.insert(drawerSettings.index, f);
              }
              setDrawerSettings(undefined);
            }}
          />
        )}
        <DrawerContent {...rest}>
          <DrawerCloseButton />
          <DrawerHeader>Revision: {revisionName}</DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel htmlFor="name">Name:</FormLabel>
                <Input
                  id="name"
                  {...register('name', {
                    required: 'Name is required',
                    minLength: {
                      value: 3,
                      message: 'Name must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message: 'Name must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <Flex py={4}>
                  <FormLabel htmlFor="accounts" flex={1}>
                    Accounts:
                  </FormLabel>
                  <IconButton
                    icon={
                      // @ts-ignore
                      <FiPlus />
                    }
                    aria-label="Add Account"
                    colorScheme="blue"
                    variant="solid"
                    onClick={() =>
                      setDrawerSettings({
                        ...drawerSettings,
                        afeId,
                        isOpen: true,
                        index: -1,
                        // budgetSheetName: revisionName,
                        row: {
                          // id: "0",
                          // afeTransactionId: "0",
                          subAccountId: "0",
                          budgetedAmount: '',
                          contingencyAmount: '',
                        },
                      })
                    }
                  />
                </Flex>
                <TableContainer>
                  <Table variant={'simple'}>
                    <Thead>
                      <Tr>
                        <Th></Th>
                        <Th>Account</Th>
                        <Th>Budget</Th>
                        <Th>Contingency</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {!accountsField.fields.length && (
                        <Tr>
                          <Td colSpan={4}>
                            <Center>No Accounts to Display</Center>
                          </Td>
                        </Tr>
                      )}
                      {accountsField.fields.map((r, i) => (
                        <Tr>
                          <Td>
                            <IconButton
                              icon={
                                // @ts-ignore
                                <FiEdit />
                              }
                              aria-label="Edit Account"
                              onClick={() =>
                                setDrawerSettings({
                                  ...drawerSettings,
                                  afeId,
                                  isOpen: true,
                                  // budgetSheetName: revisionName,
                                  index: i,
                                  row: r,
                                })
                              }
                            />
                          </Td>
                          <Td>
                            <SubAccountLabel
                              value={accounts?.items
                                .flatMap((x) => x.subAccounts)
                                .find((x) => x.subAccountId === r.subAccountId)}
                            />
                          </Td>
                          <Td>
                            <CurrencyLabel value={r.budgetedAmount} />
                          </Td>
                          <Td>
                            <CurrencyLabel value={r.contingencyAmount} />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </FormControl>
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              onCancel={onClose}
              onConfirm={handleSubmit(onSubmit)}
            />
          </DrawerFooter>
        </DrawerContent>
      </form>
    </FormProvider>
  );
};
